// src/pages/task/TaskTypeCreatePage.tsx
// Add a task type — heading, one-line description, house-style cards, primary
// action bottom right of its card. The page draws no breadcrumb of its own:
// the app top bar already shows Settings › Task types › New.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTaskTaskType, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, CardDescription, ErrorAlert } from '@react/components';
import {
  TaskTypeForm,
  taskTypeFormDefaults,
  assignmentRoleMissing,
  requiresAssignmentRole,
  type TaskTypeFormData,
} from '@/features/task/TaskTypeForm';
import { useResolvedTaskConfig } from '@/features/task/useResolvedTaskConfig';
import { inheritedReference, inheritedStatusSet } from '@/features/task/inheritance';
import { OriginLine } from '@/features/task/InheritanceNotice';
import { secondsToDuration } from '@/features/task/panelHelpers';

/**
 * Add a task type.
 * The product is taken from the current scope rather than typed as a UUID.
 */
export const TaskTypeCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useTaskTaskType();
  const { t } = useI18n();
  const scope = useScope();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formData, setFormData] = useState<TaskTypeFormData>({
    ...taskTypeFormDefaults(),
    product_id: scope.productId,
  });

  // A type that does not exist yet still inherits: whatever the scope offers is
  // exactly what this type will do on the day it is created, unless a field
  // here says otherwise. No type id to exclude — nothing is its own yet.
  const resolved = useResolvedTaskConfig();
  const inheritedStatuses = inheritedStatusSet(resolved.data?.statuses);

  const handleChange = <K extends keyof TaskTypeFormData>(field: K, value: TaskTypeFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // The backend refuses a role rule with no role. Say which field is missing,
    // in place, rather than sending a request that comes back as a banner.
    setSubmitAttempted(true);
    if (assignmentRoleMissing(formData)) return;

    setIsSubmitting(true);
    try {
      const hours = parseFloat(formData.default_estimated_hours);
      await store.RegisterTaskType({
        ...formData,
        default_assignee: formData.default_assignee_id
          ? { entity: { id: formData.default_assignee_id }, display_name: formData.default_assignee_name }
          : undefined,
        default_estimated_duration: hours > 0 ? secondsToDuration(hours * 3600) : undefined,
        // A role only travels with the rules that read it.
        assignment_role: requiresAssignmentRole(formData.assignment_rule) && formData.assignment_role_id
          ? { id: formData.assignment_role_id, type_name: 'role' }
          : undefined,
        // Left blank, both of these stay absent so the new type follows its
        // scope rather than being pinned to an empty value.
        default_pipeline: formData.default_pipeline_id ? { id: formData.default_pipeline_id } : undefined,
        default_template: formData.default_template_id ? { id: formData.default_template_id } : undefined,
      } as never);
      navigate('/settings/task-types');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !!formData.slug.trim() && !!formData.display_name.trim() && !!formData.product_id.trim();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('task.addType')}</h1>
        <p className="text-sm text-muted-foreground">{t('task.addTypeDescription')}</p>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <TaskTypeForm
        value={formData}
        onChange={handleChange}
        submitAttempted={submitAttempted}
        inheritedTemplate={inheritedReference(resolved.data?.template)}
        inheritedTemplateName={resolved.data?.templateName || ''}
        inheritedPipeline={inheritedReference(resolved.data?.pipeline)}
        inheritedPipelineName={resolved.data?.pipelineName || ''}
        askedScope={resolved.scopeEntity}
        isResolving={resolved.isLoading}
        resolveError={resolved.error}
        onRetryResolve={resolved.reload}
      />

      {/* Creation deliberately carries no lifecycle: RegisterTaskType has no
          field for status_definitions or transitions, so any editor shown here
          would collect settings the request cannot send. What it can do is stop
          guessing at what the new type will do — if the scope offers a status
          set, that is what this type gets on day one, not the platform default. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('task.lifecycle.title')}</CardTitle>
          <CardDescription>{t('task.lifecycle.createNotice')}</CardDescription>
        </CardHeader>
        <CardContent>
          {resolved.isLoading ? (
            <div className="h-10 animate-pulse rounded-md bg-muted" role="status" aria-live="polite" />
          ) : resolved.error ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('task.inheritance.statuses.resolveFailed')}
              </p>
              <Button size="sm" variant="secondary" onClick={resolved.reload}>
                {t('common.retry')}
              </Button>
            </div>
          ) : inheritedStatuses ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                {t('task.inheritance.statuses.createInherits', {
                  count: inheritedStatuses.status_definitions.length,
                })}
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {inheritedStatuses.status_definitions.map((def) => (
                  <li key={def.slug}>
                    <Badge variant="secondary" size="sm">{def.display_name || def.slug}</Badge>
                  </li>
                ))}
              </ul>
              <OriginLine
                origin={inheritedStatuses.origin}
                askedScope={resolved.scopeEntity}
                kind="statuses"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('task.lifecycle.createDefaultNotice')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">{t('task.typeForm.submitHint')}</p>
          <div className="flex items-center justify-end gap-3">
            <Link to="/settings/task-types">
              <Button type="button" variant="secondary">
                {t('common.cancel')}
              </Button>
            </Link>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit || isSubmitting} isLoading={isSubmitting}>
              {t('task.addType')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default TaskTypeCreatePage;
